import { internalMessageBus } from "sb-internal-msg-bus";
import { SportDataGateway, GlobalConfig } from "sb-bff-integration";
import { Cookies } from "sb-helpers";
import { MessageBus, Clock, MessageSender, State, queryType, TimeZoneObservable, UserInfoObservable } from "../../../common";
import { SportParser } from "../parser";
import { sportMapperFactory, liveCountMapper, preLiveCountMapper, todayCountMapper, SportsMapper, personalizedSportMapperFactory } from "../mapper";
import { SportsRetriever, FavoritesRetriever, SportPushesRetriever, LiveStreamingRetriever, LiveStreamingBaseRetriever, TodayLiveStreamingRetriever, SportsBaseRetriever, TodaySportsRetriever } from "../retriever";
import { liveSportsRequest, preliveSportsRequest, todaySportsRequest, liveLSEventsCountByTagRequest, upcomingLSEventsCountByTagRequest, todayLSEventsCountByTagRequest } from "../queries/SportRequests";
import { liveSportApiQueryFactory, preLiveSportApiQueryFactory, todaySportApiQueryFactory, personalizedSportBFFQueryFactory } from "../queries";
import { liveStreamingMode, defaultItemForSportMode } from "../sportConfig";
import { PersonalizedRetriever, PersonalizedBaseRetriever } from "../retriever/Personalized";
import { PersonalizedSportsMapper } from "../mapper/PersonalizedSportsMapper";
export const getTimeZoneObservable = () => new TimeZoneObservable(getMessageBus());
export const getUserInfoObservable = () => new UserInfoObservable(getMessageBus());
export const sportsRetrieversFactory = (config, dict, timeZoneObservable, userInfoObservable, bffGateway, connections) => {
    const sportsRetriever = getSportsRetriever(config, timeZoneObservable, userInfoObservable, dict, bffGateway, connections);
    const retrievers = [sportsRetriever];
    if (config.enableFavorites) {
        retrievers.push(new FavoritesRetriever(getMessageBus(), config.isFavoritesFirst, config.blockId, dict, Cookies));
    }
    if (config.enableLiveStreaming) {
        retrievers.push(getLiveStreamingRetriever(config, dict, timeZoneObservable, connections));
    }
    return retrievers;
};
const getSportsRetriever = (config, timeZoneObservable, userInfoObservable, dict, bffGateway, connections) => {
    switch (config.query) {
        case queryType.live:
            return getLiveSportsRetriever(config, connections);
        case queryType.preLive:
            return getPreLiveSportsRetriever(config, connections);
        case queryType.today:
            return getTodaySportRetriever(config, timeZoneObservable, connections);
        case queryType.personalized:
            return getPersonalizedSportRetriever(config, userInfoObservable, dict, bffGateway);
    }
};
const getLiveStreamingRetriever = (config, dict, timeZoneObservable, connections) => {
    const baseRetriever = new LiveStreamingBaseRetriever(connections, dict, config.liveStreamingUpdatePeriod, config.liveStreamingMode);
    switch (config.query) {
        case queryType.live:
            return new LiveStreamingRetriever(baseRetriever, liveLSEventsCountByTagRequest);
        case queryType.preLive:
            return new LiveStreamingRetriever(baseRetriever, upcomingLSEventsCountByTagRequest(new Date().toISOString()));
        case queryType.today:
            return new TodayLiveStreamingRetriever(baseRetriever, timeZoneObservable, new Clock(Date), todayLSEventsCountByTagRequest);
        default:
            return new LiveStreamingRetriever(baseRetriever, upcomingLSEventsCountByTagRequest(new Date().toISOString()));
    }
};
const getLiveSportsRetriever = (config, connections) => {
    const sportApiQueryFactory = liveSportApiQueryFactory(GlobalConfig.apiConfigs.locale);
    const countMapper = liveCountMapper;
    const sportMapper = sportMapperFactory(countMapper, config.sportOrder);
    const sportsMapper = new SportsMapper(sportMapper);
    const sportPushesRetriever = new SportPushesRetriever(sportsMapper, countMapper, new SportDataGateway());
    const baseRetriever = new SportsBaseRetriever(connections, sportsMapper, sportPushesRetriever, config.sportRetrieverUpdatePeriod);
    return new SportsRetriever(baseRetriever, liveSportsRequest, sportApiQueryFactory);
};
const getMessageBus = () => new MessageBus(internalMessageBus);
const getPreLiveSportsRetriever = (config, connections) => {
    const sportApiQueryFactory = preLiveSportApiQueryFactory(GlobalConfig.apiConfigs.locale, Date);
    const countMapper = preLiveCountMapper;
    const sportMapper = sportMapperFactory(countMapper, config.sportOrder);
    const sportsMapper = new SportsMapper(sportMapper);
    const sportPushesRetriever = new SportPushesRetriever(sportsMapper, countMapper, new SportDataGateway());
    const baseRetriever = new SportsBaseRetriever(connections, sportsMapper, sportPushesRetriever, config.sportRetrieverUpdatePeriod);
    return new SportsRetriever(baseRetriever, preliveSportsRequest(Date), sportApiQueryFactory);
};
const getTodaySportRetriever = (config, timeZoneObservable, connections) => {
    const countMapper = todayCountMapper;
    const sportMapper = sportMapperFactory(countMapper, config.sportOrder);
    const sportsMapper = new SportsMapper(sportMapper);
    const sportPushesRetriever = new SportPushesRetriever(sportsMapper, countMapper, new SportDataGateway());
    const baseRetriever = new SportsBaseRetriever(connections, sportsMapper, sportPushesRetriever, config.sportRetrieverUpdatePeriod);
    return new TodaySportsRetriever(baseRetriever, timeZoneObservable, new Clock(Date), todaySportsRequest, todaySportApiQueryFactory(GlobalConfig.apiConfigs.locale));
};
const getPersonalizedSportRetriever = (config, userInfoObservable, dict, bffGateway) => {
    const personalizationConfig = Object.assign({}, config.personalization);
    const personalizedSportMapper = personalizedSportMapperFactory(config.sportOrder);
    const personalizedSportsMapper = new PersonalizedSportsMapper(personalizedSportMapper);
    const baseRetriever = new PersonalizedBaseRetriever(
    // connections,
    bffGateway, dict, config.sportRetrieverUpdatePeriod, personalizedSportsMapper, personalizationConfig.enableForYouTab, personalizationConfig.maxPersonalizedSports, personalizationConfig.maxPersonalizedEventsPerSport, personalizationConfig.regionCode);
    return new PersonalizedRetriever(baseRetriever, personalizedSportBFFQueryFactory, userInfoObservable);
};
const isFavoritesShouldBeSelected = (config) => {
    return config.isFavoritesFirst && config.enableFavorites && !isLiveStreamingShouldBeSelected(config);
};
const isLiveStreamingShouldBeSelected = (config) => {
    return config.enableLiveStreaming && config.liveStreamingMode === liveStreamingMode.firstSelected;
};
const isForYouTabShouldBeSelected = (config) => {
    return config.query === queryType.personalized && config.personalization.enableForYouTab;
};
const getSelectedSportByConfig = (config) => {
    if (isFavoritesShouldBeSelected(config)) {
        return defaultItemForSportMode.favorites;
    }
    if (isLiveStreamingShouldBeSelected(config)) {
        return defaultItemForSportMode.liveStreaming;
    }
    if (isForYouTabShouldBeSelected(config)) {
        return defaultItemForSportMode.forYou;
    }
    return defaultItemForSportMode.firstSport;
};
export const sportsParserFactory = (config, retrievers) => {
    return new SportParser(retrievers, getMessageBus(), config.blockId);
};
export const sportsStateFactory = (config, domId) => {
    const messageSender = new MessageSender(getMessageBus(), config.blockId);
    return new State(messageSender, config.blockId, domId, getSelectedSportByConfig(config));
};
//# sourceMappingURL=factories.js.map